// Nudging players toward a controller (#161), sim- and DOM-free: the game
// is tuned for a pad, so until one has been seen on this device the start
// screen suggests one (dismissable, so at most once per page load) and the
// Controls panel opens with a one-line tip. Once a pad connects the
// nudges are gone for good: `InputManager.padSeen`, remembered under
// `PAD_SEEN_KEY`. Never during play.

export const PAD_SEEN_KEY = "kickflip.padSeen";

/** Desktop wording: works for Xbox, PlayStation and Switch Pro pads in every major browser. */
export const PAD_NUDGE = "Best with a controller: plug one in or pair it, then press any button";

/** Phones pair Bluetooth pads too; only said once the touch tutorial is done. */
export const PHONE_PAD_NUDGE = "Got a Bluetooth controller? It plays better: pair it, then press any button";

export function padSeenBefore(store: Storage | null): boolean {
  try {
    return store?.getItem(PAD_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

export function markPadSeen(store: Storage | null): void {
  try {
    store?.setItem(PAD_SEEN_KEY, "1");
  } catch {
    // Storage full or refused: the nudge comes back next visit, nothing worse.
  }
}

export interface NudgeContext {
  /** The start screen's line, or the Controls panel's tip. */
  where: "start" | "controls";
  /** A pad has connected on this device, now or before. */
  padSeen: boolean;
  /** A phone or tablet (touch wording). */
  touch: boolean;
  /** The touch tutorial has been done or skipped (phones only hear about pads after it). */
  tutorialSeen: boolean;
  /** The start screen's line was closed this page load. */
  dismissed: boolean;
}

/** The nudge to show, or null. */
export function controllerNudge(c: NudgeContext): string | null {
  if (c.padSeen) return null;
  if (c.touch && !c.tutorialSeen) return null;
  if (c.where === "start" && c.dismissed) return null;
  return c.touch ? PHONE_PAD_NUDGE : PAD_NUDGE;
}
